import { BoltIcon, ChevronDownIcon } from '@heroicons/react/16/solid';
import { useFormikContext } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WAPI_EVENTS } from 'src/hooks/analytics/wapiAnalytics.constants';
import { useWapiAnalytics } from 'src/hooks/analytics/useWapiAnalytics.hook';
import { eAlertType } from '../../../../../../../../providers/ToastProvider/Toast.enum';
import { useToastContext } from '../../../../../../../../providers/ToastProvider/Toast.hook';
import { useACLContext } from '../../../../../../../../providers/ACLProvider/ACL.hook';
import { useUserContext } from '../../../../../../../../providers/UserProvider/User.context';
import { useWorkspaceContext } from '../../../../../../../../providers/WorkspaceProvider/Workspace.context';
import { useChatContext } from '../../../../../../Chat.hook';
import { ChatService } from '../../../../../../Chat.service';
import {
  tInteractiveOutbound,
  tNewQuickAction,
} from '../../../../../../Chat.types';
import { toQuickReplyInteractiveConfig } from '../../InteractiveAction.functions';

// Matches the shortCommand convention elsewhere — lowercase, no spaces, no
// leading slash (we render the / prefix). Keeps the rule tight so two
// sellers can't accidentally create `/promo` and `/Promo` as distinct
// commands that look identical in the list.
const SHORT_COMMAND_REGEX = /^[a-z0-9_-]+$/;
const SHORT_COMMAND_MAX_LENGTH = 40;

type tSaveState =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'error'; message: string };

// Compact "Save as quick reply" form that lives at the bottom of the
// preview pane. Consumes the Formik draft + isValid, translates to the
// quick-replies-service `interactive_config` shape, and POSTs via the
// shared createQuickReply service. Disabled until the draft is valid so
// sellers can't persist broken templates.
export default function PreviewSaveQuickReply() {
  const { t } = useTranslation(['chat/chat']);
  const formik = useFormikContext<tInteractiveOutbound>();
  const { currentWorkspaceId } = useWorkspaceContext();
  const { currentUser } = useUserContext();
  const { canEditInteractiveFooter } = useACLContext();
  const { addAlert } = useToastContext();
  const { trackWapiEvent } = useWapiAnalytics();
  const { fetchQuickReplies } = useChatContext();

  // Collapsed by default — keeps the preview pane focused on the bubble
  // until the seller explicitly wants to persist the draft as a reusable
  // template. Expanded once and left open for the remainder of the
  // composer session so subsequent saves don't re-toggle.
  const [isExpanded, setIsExpanded] = useState(false);
  const [commandHandle, setCommandHandle] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [saveState, setSaveState] = useState<tSaveState>({ kind: 'idle' });

  const trimmedHandle = commandHandle.trim();
  const handleError =
    trimmedHandle === ''
      ? null
      : !SHORT_COMMAND_REGEX.test(trimmedHandle)
        ? t('interactive.saveAsQuickReply.errorFormat')
        : trimmedHandle.length > SHORT_COMMAND_MAX_LENGTH
          ? t('interactive.saveAsQuickReply.errorLength', {
              max: SHORT_COMMAND_MAX_LENGTH,
            })
          : null;

  const canSave =
    formik.isValid &&
    trimmedHandle !== '' &&
    !handleError &&
    saveState.kind !== 'saving';

  async function handleSave() {
    if (!canSave) return;
    if (!currentWorkspaceId || !currentUser?.authId) {
      setSaveState({
        kind: 'error',
        message: t('interactive.saveAsQuickReply.errorNoWorkspace'),
      });
      return;
    }

    setSaveState({ kind: 'saving' });

    const payload: tNewQuickAction = {
      short_command_handle: trimmedHandle,
      // Backend `forbidden_with_interactive` cross-field validator rejects
      // a payload with BOTH interactive_config and non-empty message /
      // attachment. Send both empty for interactive saves — authoritative
      // content lives in interactive_config.
      message: '',
      auth_id: currentUser.authId,
      workspace_id: currentWorkspaceId,
      is_public: isPublic,
      attachment: [],
      interactive_config: toQuickReplyInteractiveConfig(formik.values, {
        canEditFooter: canEditInteractiveFooter,
      }),
    };

    try {
      await ChatService.createQuickReply({ data: payload });
      fetchQuickReplies();
      trackWapiEvent(WAPI_EVENTS.CHAT_INTERACTIVE_QUICK_REPLY_SAVE_SUCCESS, {
        type: formik.values.type,
        isPublic,
      });
      addAlert({
        type: eAlertType.SUCCESS,
        message: t('interactive.saveAsQuickReply.successToast', {
          handle: trimmedHandle,
        }),
      });
      setCommandHandle('');
      setSaveState({ kind: 'idle' });
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 409) {
        setSaveState({
          kind: 'error',
          message: t('interactive.saveAsQuickReply.errorAlreadyExists'),
        });
        trackWapiEvent(
          WAPI_EVENTS.CHAT_INTERACTIVE_QUICK_REPLY_SAVE_REJECTED,
          { type: formik.values.type, reason: 'DUPLICATE_HANDLE' },
        );
        return;
      }
      setSaveState({
        kind: 'error',
        message: t('interactive.saveAsQuickReply.errorGeneric'),
      });
      trackWapiEvent(WAPI_EVENTS.CHAT_INTERACTIVE_QUICK_REPLY_SAVE_REJECTED, {
        type: formik.values.type,
        reason: 'NETWORK_ERROR',
      });
    }
  }

  if (!isExpanded) {
    return (
      <button
        type="button"
        className="InteractiveAction__SaveAsQuickReply__Toggle"
        onClick={() => setIsExpanded(true)}
        aria-expanded="false"
      >
        <BoltIcon className="InteractiveAction__SaveAsQuickReply__Toggle__Icon" />
        <span className="InteractiveAction__SaveAsQuickReply__Toggle__Label">
          {t('interactive.saveAsQuickReply.title')}
        </span>
        <ChevronDownIcon className="InteractiveAction__SaveAsQuickReply__Toggle__Caret" />
      </button>
    );
  }

  return (
    <form
      className="InteractiveAction__SaveAsQuickReply"
      onSubmit={(e) => {
        e.preventDefault();
        handleSave();
      }}
    >
      <button
        type="button"
        className="InteractiveAction__SaveAsQuickReply__Header"
        onClick={() => setIsExpanded(false)}
        aria-expanded="true"
        aria-label={t('interactive.saveAsQuickReply.collapse')}
      >
        <BoltIcon className="InteractiveAction__SaveAsQuickReply__Header__Icon" />
        <span className="InteractiveAction__SaveAsQuickReply__Header__Title">
          {t('interactive.saveAsQuickReply.title')}
        </span>
        <ChevronDownIcon className="InteractiveAction__SaveAsQuickReply__Header__Caret" />
      </button>

      <div className="InteractiveAction__SaveAsQuickReply__HandleRow">
        <span className="InteractiveAction__SaveAsQuickReply__HandlePrefix">
          /
        </span>
        <input
          type="text"
          dir="auto"
          maxLength={SHORT_COMMAND_MAX_LENGTH}
          className="InteractiveAction__SaveAsQuickReply__HandleInput"
          placeholder={t('interactive.saveAsQuickReply.handlePlaceholder')}
          value={commandHandle}
          onChange={(e) => {
            setCommandHandle(e.target.value.toLowerCase());
            if (saveState.kind === 'error') setSaveState({ kind: 'idle' });
          }}
          aria-invalid={handleError ? 'true' : 'false'}
          aria-describedby={
            handleError
              ? 'InteractiveAction__SaveAsQuickReply__HandleError'
              : undefined
          }
        />
      </div>
      {handleError ? (
        <p
          id="InteractiveAction__SaveAsQuickReply__HandleError"
          className="InteractiveAction__SaveAsQuickReply__FieldError"
        >
          {handleError}
        </p>
      ) : null}

      <div className="InteractiveAction__SaveAsQuickReply__VisibilityRow">
        <label className="InteractiveAction__SaveAsQuickReply__VisibilityOption">
          <input
            type="radio"
            name="interactive-save-visibility"
            className="radio radio-xs"
            checked={isPublic}
            onChange={() => setIsPublic(true)}
          />
          <span>{t('interactive.saveAsQuickReply.visibilityPublic')}</span>
        </label>
        <label className="InteractiveAction__SaveAsQuickReply__VisibilityOption">
          <input
            type="radio"
            name="interactive-save-visibility"
            className="radio radio-xs"
            checked={!isPublic}
            onChange={() => setIsPublic(false)}
          />
          <span>{t('interactive.saveAsQuickReply.visibilityPrivate')}</span>
        </label>
      </div>

      {saveState.kind === 'error' ? (
        <p className="InteractiveAction__SaveAsQuickReply__FieldError">
          {saveState.message}
        </p>
      ) : null}

      <button
        type="submit"
        className="InteractiveAction__SaveAsQuickReply__SaveButton"
        disabled={!canSave}
      >
        {saveState.kind === 'saving'
          ? t('interactive.saveAsQuickReply.saving')
          : t('interactive.saveAsQuickReply.save')}
      </button>
    </form>
  );
}
